import { ControlCodes } from '@bcube/shared/constants';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Settings, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { accountApi } from '@/api/commercial/accounts';
import { type ISector, sectorApi } from '@/api/commercial/sectors';
import { EconomicGroupManagementDialog } from '@/components/commercial/accounts/economic-group-management-dialog';
import { SectorManagementDialog } from '@/components/commercial/accounts/sector-management-dialog';
import { AccountTable } from '@/components/table/commercial/account-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth-context';

export function AccountListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { restrictFeatureByProfile } = useAuth();

  const hasPermissionNewAccount = restrictFeatureByProfile(ControlCodes.commercial_accounts_new);

  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [economicGroupDialogOpen, setEconomicGroupDialogOpen] = useState(false);

  // Filter states
  const [filterClient, setFilterClient] = useState(searchParams.get('search') || '');
  const [filterDocument, setFilterDocument] = useState(searchParams.get('document') || '');
  const [filterSector, setFilterSector] = useState(searchParams.get('sector') || '');

  const page = Number(searchParams.get('page')) || 1;

  const { data: sectorsData, isLoading: sectorsLoading } = useQuery({
    queryKey: ['sectors', { enabled: true }],
    queryFn: () => sectorApi.findMany({ enabled: true, limit: 100 }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', Object.fromEntries(searchParams)],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      const search = searchParams.get('search');
      const document = searchParams.get('document');
      const sector = searchParams.get('sector');
      const pageParam = searchParams.get('page');

      const rawDocument = document?.replace(/\D/g, '') || '';
      const searchTerms = [search, rawDocument].filter(Boolean);
      if (searchTerms.length > 0) {
        params.search = searchTerms.join(' ');
      }
      if (sector) params.sector = sector;
      if (pageParam) params.page = Number(pageParam);

      return accountApi.findMany(params);
    },
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    // Set all filters independently to the URL params
    if (filterClient) params.set('search', filterClient);
    if (filterDocument) params.set('document', filterDocument);
    if (filterSector) params.set('sector', filterSector);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterClient('');
    setFilterDocument('');
    setFilterSector('');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar contas</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Contas</h1>
          <p className="text-muted-foreground">Visualize e gerencie todas as contas cadastradas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEconomicGroupDialogOpen(true)}>
            <Users className="h-4 w-4 mr-2" />
            Administrar Grupos
          </Button>
          <Button variant="outline" onClick={() => setSectorDialogOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Setores
          </Button>
          {hasPermissionNewAccount && (
            <Button asChild className="bg-black text-white hover:bg-gray-800">
              <Link to="/commercial/accounts/create">
                <Plus className="h-4 w-4 mr-2" />
                Nova Conta
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Preencha os filtros abaixo e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Cliente"
                  placeholder="Filtrar por nome"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="CPF/CNPJ"
                  placeholder="Filtrar por documento"
                  value={filterDocument}
                  onChange={(e) => setFilterDocument(e.target.value)}
                />
              </div>
              <div>
                {/* biome-ignore lint/a11y/noLabelWithoutControl: Combobox renders its own interactive trigger */}
                <label className="block text-sm font-medium text-gray-700 mb-2">Setor</label>
                <Combobox<ISector>
                  data={sectorsData?.docs ?? []}
                  keyExtractor={(s) => s.name}
                  displayValueGetter={(s) => s.name}
                  placeholder="Selecione um setor"
                  type="single"
                  isLoading={sectorsLoading}
                  value={
                    filterSector
                      ? (sectorsData?.docs.filter((s) => s.name === filterSector) ?? [])
                      : []
                  }
                  onOptionChange={(selected) => {
                    setFilterSector(selected.length > 0 ? selected[0].name : '');
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <AccountTable data={data} currentPage={page} onPageChange={handlePageChange} />
      ) : null}

      <SectorManagementDialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen} />
      <EconomicGroupManagementDialog
        open={economicGroupDialogOpen}
        onOpenChange={setEconomicGroupDialogOpen}
      />
    </motion.div>
  );
}
